import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { Card } from '@/types'
import './ContributionHeatmap.css'

interface DayData {
  date: string
  count: number
  level: 0 | 1 | 2 | 3 | 4
}

interface ContributionHeatmapProps {
  cards: Card[]
}

function aggregateCardsByDay(cards: Card[]): Map<string, number> {
  const counts = new Map<string, number>()

  cards.forEach((card) => {
    // Exclude inbox cards
    if (card.topicId === 'inbox') return

    // Extract date portion from createdAt (ISO format)
    const date = card.createdAt.split('T')[0]
    counts.set(date, (counts.get(date) || 0) + 1)
  })

  return counts
}

function calculateLevel(count: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (count === 0) return 0
  if (max <= 0) return 0

  // Quartile thresholds
  const q1 = Math.ceil(max * 0.25)
  const q2 = Math.ceil(max * 0.5)
  const q3 = Math.ceil(max * 0.75)

  if (count <= q1) return 1
  if (count <= q2) return 2
  if (count <= q3) return 3
  return 4
}

function generateHeatmapData(cardCounts: Map<string, number>): DayData[][] {
  const weeks: DayData[][] = []
  const today = new Date()
  const startDate = new Date(today)

  // Go back 52 weeks, starting from the beginning of that week (Sunday)
  startDate.setDate(startDate.getDate() - 52 * 7 - today.getDay())

  // Find max count for level calculation
  const maxCount = Math.max(...Array.from(cardCounts.values()), 1)

  let currentDate = new Date(startDate)
  let currentWeek: DayData[] = []

  while (currentDate <= today) {
    const dateStr = currentDate.toISOString().split('T')[0]
    const count = cardCounts.get(dateStr) || 0

    currentWeek.push({
      date: dateStr,
      count,
      level: calculateLevel(count, maxCount),
    })

    // Start new week on Sunday (after 7 days)
    if (currentWeek.length === 7) {
      weeks.push(currentWeek)
      currentWeek = []
    }

    currentDate.setDate(currentDate.getDate() + 1)
  }

  // Push final partial week if any
  if (currentWeek.length > 0) {
    weeks.push(currentWeek)
  }

  return weeks
}

interface MonthLabel {
  label: string
  weekIndex: number
}

function generateMonthLabels(weeks: DayData[][], locale: string): MonthLabel[] {
  const labels: MonthLabel[] = []
  let lastMonth = -1

  weeks.forEach((week, weekIndex) => {
    if (week.length > 0) {
      // Check if this week contains the 1st of a month
      const hasFirstOfMonth = week.some((day) => {
        const date = new Date(day.date)
        return date.getDate() === 1
      })

      if (hasFirstOfMonth) {
        // Find the day that is the 1st
        const firstOfMonthDay = week.find((day) => {
          const date = new Date(day.date)
          return date.getDate() === 1
        })

        if (firstOfMonthDay) {
          const date = new Date(firstOfMonthDay.date)
          const month = date.getMonth()

          if (month !== lastMonth) {
            labels.push({
              label: date.toLocaleDateString(locale, { month: 'short' }),
              weekIndex,
            })
            lastMonth = month
          }
        }
      }
    }
  })

  return labels
}

function formatTooltipDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function ContributionHeatmap({ cards }: ContributionHeatmapProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'zh-TW' ? 'zh-TW' : 'en-US'

  const [tooltip, setTooltip] = useState<{
    date: string
    count: number
    x: number
    y: number
  } | null>(null)

  // Memoize data transformation
  const heatmapData = useMemo(() => {
    const counts = aggregateCardsByDay(cards)
    return generateHeatmapData(counts)
  }, [cards])

  // Generate month labels
  const monthLabels = useMemo(
    () => generateMonthLabels(heatmapData, locale),
    [heatmapData, locale]
  )

  // Calculate total contributions in the year
  const totalContributions = useMemo(() => {
    return heatmapData.flat().reduce((sum, day) => sum + day.count, 0)
  }, [heatmapData])

  const handleCellHover = (e: React.MouseEvent, day: DayData) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setTooltip({
      date: day.date,
      count: day.count,
      x: rect.left + rect.width / 2,
      y: rect.top,
    })
  }

  const handleCellLeave = () => {
    setTooltip(null)
  }

  return (
    <div className="contribution-heatmap">
      <div className="heatmap-header">
        <span className="heatmap-total">
          {t('dashboard.activity.total', { count: totalContributions })}
        </span>
      </div>

      <div className="heatmap-container">
        {/* Month labels */}
        <div className="heatmap-months">
          {monthLabels.map((month, idx) => (
            <span
              key={idx}
              className="heatmap-month-label"
              style={{ left: month.weekIndex * 14 }}
            >
              {month.label}
            </span>
          ))}
        </div>

        {/* Grid with day labels */}
        <div className="heatmap-grid-wrapper">
          <div className="heatmap-day-labels">
            <span></span>
            <span>Mon</span>
            <span></span>
            <span>Wed</span>
            <span></span>
            <span>Fri</span>
            <span></span>
          </div>

          <div className="heatmap-grid">
            {heatmapData.map((week, weekIndex) => (
              <div key={weekIndex} className="heatmap-week">
                {week.map((day) => (
                  <div
                    key={day.date}
                    className={`heatmap-cell heatmap-cell--level-${day.level}`}
                    onMouseEnter={(e) => handleCellHover(e, day)}
                    onMouseLeave={handleCellLeave}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="heatmap-legend">
          <span>{t('dashboard.activity.less')}</span>
          <div className="heatmap-legend-cells">
            {[0, 1, 2, 3, 4].map((level) => (
              <div
                key={level}
                className={`heatmap-cell heatmap-cell--level-${level}`}
              />
            ))}
          </div>
          <span>{t('dashboard.activity.more')}</span>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="heatmap-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <strong>
            {tooltip.count} {t('dashboard.activity.cards', { count: tooltip.count })}
          </strong>
          <span>{formatTooltipDate(tooltip.date, locale)}</span>
        </div>
      )}
    </div>
  )
}

export default ContributionHeatmap
